import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import { BehaviorSubject, finalize, Subject, takeUntil } from 'rxjs';

// Configs
import { LocalStorageKeys } from 'v2/modules/shared/interfaces/localstorage.interface';
import { ShipstationApiService } from 'v2/modules/shared/services/api/shipstation-api.service';
import { AgGridStateUtils } from 'v2/modules/shared/util/ag-grid-state.utils';
import { AgGridUtils } from 'v2/modules/shared/util/ag-grid.utils';

import { ShipmentsGridConfig } from '../../configs/shipments-grid.config';

// Constants
import { ShipmentsGridConstants } from '../../constants/shipments-grid.constants';

// Services
import { ShipmentsGridActionService } from '../../services/shipments-grid-action.service';

// Types
import {
  EShipmentsGridField,
  IShipmentsGridColumnDefContext,
  IShipmentsGridOptionsContext,
} from '../../types/shipments-grid.types';

// Utils

import { ShipmentsGridActionsComponent } from '../shipments-grid-actions/shipments-grid-actions.component';

@Component({
  selector: 'lum-shipments-ag-table',
  standalone: true,
  imports: [CommonModule, AgGridModule, ShipmentsGridActionsComponent],
  templateUrl: './shipments-ag-table.component.html',
  styleUrls: ['./shipments-ag-table.component.scss'],
})
export class ShipmentsAgTableComponent implements OnInit {
  @Output() selectedRowsChange = new EventEmitter<number[]>();

  @Input() showGridActions :boolean = false;
  @Input() salesOrderId :number;
  @Input() height :string = '100%';
  @Input() width :string = '100%';

  columnDefs :ColDef[];
  gridOptions :GridOptions;
  selectedRowIds$ = new BehaviorSubject<number[]>([]);

  private gridApi :GridApi;
  private onDestroy$ = new Subject();

  constructor(
    private shipstationApiService :ShipstationApiService,
    private shipmentsGridActionService :ShipmentsGridActionService,
  ) {}

  ngOnInit() :void {
    this.initColumnDefs();
    this.initGridOptions();
  }

  onGridReady(params :GridReadyEvent) :void {
    this.shipmentsGridActionService.setGridApi(params.api);
    this.gridApi = params.api;
    this.gridApi.setServerSideDatasource(this.getServerSideDatasource());
    this.initDetailRowData();
    AgGridStateUtils.loadAgGridState(
      this.gridOptions,
      this.columnDefs,
      LocalStorageKeys.SHIPMENTS_AG_STATE,
    );
  }

  getServerSideDatasource() :IServerSideDatasource {
    return {
      getRows: (params :IServerSideGetRowsParams) => {
        this.gridOptions.api?.showLoadingOverlay();
        let requestParams = AgGridUtils.mapAgGridRequestParams(
          params,
          ShipmentsGridConstants,
          EShipmentsGridField.ID,
          this.gridOptions.paginationPageSize,
        );

        if (this.salesOrderId) {
          requestParams = AgGridUtils.addFiltersToRequestParams(requestParams, [
            {
              column: 'mtShipstationOrderId',
              operator: 'eq',
              value: this.salesOrderId.toString(),
            },
          ]);
        }

        this.shipstationApiService
          .getShipments(requestParams)
          .pipe(
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.gridOptions.api?.hideOverlay();
            }),
          )
          .subscribe({
            next: (res) => {
              params.successCallback(res.data, res.meta.total);
            },
            error: () => {
              params.failCallback();
            },
          });
      },
    };
  }

  onRefreshTable() :void {
    this.shipmentsGridActionService.refreshGrid();
  }

  private initColumnDefs() :void {
    // context here is used to pass any data/function to the column definition
    const columnDefContext :IShipmentsGridColumnDefContext = {
      dropdownOptions: this.shipmentsGridActionService.getDropdownActions(),
    };
    this.columnDefs =
      ShipmentsGridConfig.getShipmentsGridColumnConfig(columnDefContext);
  }

  private initGridOptions() :void {
    // context here is used to pass any data/function to the grid options
    const gridOptionsContext :IShipmentsGridOptionsContext = {
      saveGridState: () => {
        AgGridStateUtils.saveAgGridState(
          this.gridOptions,
          this.columnDefs,
          LocalStorageKeys.SHIPMENTS_AG_STATE,
        );
      },
      refetchGridData: () => this.shipmentsGridActionService.refreshGrid(),
      updateSelectedRows: () => {
        this.updateSelectedRows();
      },
      fetchShipmentDetails: (shipmentId :number) => {
        return this.shipstationApiService.getShipmentItems(shipmentId);
      },
    };
    this.gridOptions =
      ShipmentsGridConfig.getShipmentsGridOptions(gridOptionsContext);
  }

  private updateSelectedRows() :void {
    const selectedIds :number[] = [];

    this.gridApi.forEachNode((node) => {
      if (node.isSelected() && node.data?.id) {
        selectedIds.push(node.data.id);
      }
    });

    this.selectedRowIds$.next(selectedIds);
  }

  private initDetailRowData() :void {
    this.gridOptions.detailCellRendererParams.getDetailRowData = (params) => {
      if (params.data.shipmentItems && params.data.shipmentItems.length > 0) {
        params.successCallback(params.data.shipmentItems);

        return;
      }

      this.shipstationApiService
        .getShipmentItems(params.data.id)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (details) => {
            params.data.shipmentItems = details; // Store data in row
            params.successCallback(details);
          },
          error: () => {
            params.failCallback();
          },
        });
    };
  }
}
